import { Archive, ArrowDownToLine, LoaderCircle } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { PROFILE_FORM_STYLES } from "@/components/Forms/profile-form-styles";
import { getProviderOfferLabel } from "@/features/credentials/lib/provider-import-actions";
import { ProviderOfferSummary } from "./provider-offer-summary";
import {
  ProviderOfferAction,
  type ProviderOfferConfirmation,
  type ProviderOfferProgress,
} from "./provider-import-types";

export function ProviderImportConfirmation({
  confirmation,
  progress,
  onCancel,
  onConfirm,
}: {
  confirmation: ProviderOfferConfirmation;
  progress: ProviderOfferProgress | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const importing = confirmation.action === ProviderOfferAction.Import;
  const action = importing ? "Import" : "Dismiss";
  const Icon = importing ? ArrowDownToLine : Archive;
  const count = confirmation.offers.length;
  const busy = progress !== null;
  return (
    <AlertDialog
      open
      onOpenChange={(open) => {
        if (!open && !busy) {
          onCancel();
        }
      }}
    >
      <AlertDialogContent
        className={PROFILE_FORM_STYLES.confirmation}
        onEscapeKeyDown={(event) => {
          if (busy) {
            event.preventDefault();
          }
        }}
      >
        <AlertDialogHeader className="text-left">
          <div className={PROFILE_FORM_STYLES.icon}>
            <Icon className="size-5" aria-hidden="true" />
          </div>
          <AlertDialogTitle className="pt-2 text-xl font-semibold">
            {action} {count} {count === 1 ? "update" : "updates"}?
          </AlertDialogTitle>
          <AlertDialogDescription className="sr-only">
            {count} provider {count === 1 ? "update" : "updates"}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <ul className="max-h-60 space-y-2 overflow-y-auto rounded-xl border bg-slate-50 p-4 text-sm text-slate-700">
          {confirmation.offers.map((offer) => (
            <li key={offer.id} className="space-y-1">
              <p className="font-medium">{getProviderOfferLabel(offer)}</p>
              {importing && (
                <ProviderOfferSummary offer={offer} stale={false} />
              )}
            </li>
          ))}
        </ul>
        <AlertDialogFooter className="mt-2 gap-3 sm:space-x-0">
          <AlertDialogCancel disabled={busy} onClick={onCancel}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={busy}
            className="gap-2"
            onClick={(event) => {
              event.preventDefault();
              onConfirm();
            }}
          >
            {busy && (
              <LoaderCircle
                className="size-4 animate-spin"
                aria-hidden="true"
              />
            )}
            {busy ? `${progress.completed} / ${count}` : `${action} all`}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
